import React from "react";

/**
 * Brand marks for the channels a row of hours can be reported on.
 *
 * Traced from the same artwork ERP renders in the demand queue
 * (`apps/web/components/icons/*` in bonliva-erp), so the same provider looks
 * the same in both systems. Inline SVG rather than image files: these are drawn
 * at 16px inside a table row, they must not cost a request, and a missing asset
 * would leave a broken-image box beside a payroll number.
 *
 * Each is a square tile so a row of mixed channels lines up on a common edge —
 * a logo that is wider than it is tall makes the column ragged.
 */

type IconProps = { className?: string; title?: string };

const TILE: React.CSSProperties = { display: "block", borderRadius: 3 };

export function BemloMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#0E55B8" />
      <path
        d="M219.692 448C202.019 448 187.692 433.673 187.692 416V324.308H96C78.3269 324.308 64 309.981 64 292.308V219.692C64 202.019 78.3269 187.692 96 187.692H187.692V96C187.692 78.3269 202.019 64 219.692 64H292.308C309.981 64 324.308 78.3269 324.308 96V187.692H416C433.673 187.692 448 202.019 448 219.692V292.308C448 309.981 433.673 324.308 416 324.308H324.309L324.308 187.692H187.692L187.694 390.771L244.783 333.682C250.784 327.681 258.923 324.309 267.41 324.309L324.309 324.308L324.308 416C324.308 433.673 309.981 448 292.308 448H219.692Z"
        fill="white"
      />
    </svg>
  );
}

/**
 * Medlo's mark is a wordmark rather than a glyph, so ERP renders the raster
 * logo on a brand tile. At 16px the lettering would be unreadable, so this
 * draws the initial on the same brand green instead of shipping an image that
 * resolves to a smudge.
 */
export function MedloMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#4DB6A0" />
      <path
        d="M120 372V140h58l78 122 78-122h58v232h-56V236l-64 100h-32l-64-100v136h-56Z"
        fill="white"
      />
    </svg>
  );
}

export function InkopioMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#5A2382" />
      {/* The coral disc sits behind the white ring, so the ring's stroke cuts
          across it the way it does in the real mark. */}
      <g transform="translate(116.8 65.2) scale(2.4)">
        <circle cx="83" cy="46" r="28" fill="#EB505F" />
        <circle cx="48" cy="48" r="34" stroke="#FFFFFF" strokeWidth="16" />
        <path d="M48 113V147" stroke="#FFFFFF" strokeWidth="15" strokeLinecap="round" />
        <path d="M83 113V147" stroke="#EB505F" strokeWidth="15" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/**
 * Paper — a signed sheet. The channels below have no brand behind them, so
 * they get a plain glyph on a neutral tile: the point of the column is to tell
 * channels apart at a glance, and a marketplace logo beside a generic one reads
 * as the difference it is.
 */
export function PaperMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#8D6E63" />
      <path
        d="M160 96h136l56 56v264a24 24 0 0 1-24 24H160a24 24 0 0 1-24-24V120a24 24 0 0 1 24-24Z"
        fill="white"
      />
      <path d="M296 96v56h56" fill="#D7CCC8" />
      <path
        d="M176 320c24-16 40 16 64 0s40 16 64 0 40 16 56 4"
        stroke="#8D6E63"
        strokeWidth="18"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** The consultant app — a phone. */
export function MobileAppMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#2E7D32" />
      <rect x="168" y="88" width="176" height="336" rx="28" fill="white" />
      <rect x="196" y="128" width="120" height="216" rx="8" fill="#2E7D32" />
      <circle cx="256" cy="382" r="18" fill="#2E7D32" />
    </svg>
  );
}

/** Typed in the CRM by a person — a pen. */
export function CrmMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#455A64" />
      <path
        d="M136 336l160-160 40 40-160 160-56 16 16-56Z"
        fill="white"
      />
      <path d="M312 160l24-24a28 28 0 0 1 40 40l-24 24-40-40Z" fill="#CFD8DC" />
      <path d="M136 400h240" stroke="white" strokeWidth="24" strokeLinecap="round" />
    </svg>
  );
}

/** Entered directly in ERP by a user — a keyboard. */
export function ManualMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect width="512" height="512" rx="96" fill="#5C6BC0" />
      <rect x="88" y="160" width="336" height="192" rx="24" fill="white" />
      <g fill="#5C6BC0">
        <rect x="128" y="200" width="40" height="32" rx="6" />
        <rect x="184" y="200" width="40" height="32" rx="6" />
        <rect x="240" y="200" width="40" height="32" rx="6" />
        <rect x="296" y="200" width="40" height="32" rx="6" />
        <rect x="352" y="200" width="32" height="32" rx="6" />
        <rect x="160" y="288" width="192" height="32" rx="10" />
      </g>
    </svg>
  );
}

/**
 * No channel recorded. Rows written before provenance existed carry this, and
 * it has to read as an absence rather than as a channel called "unknown" — so
 * it is an outline, not a filled brand tile.
 */
export function UnknownSourceMark({ className, title }: IconProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      className={className}
      style={TILE}
      role="img"
      aria-label={title}
    >
      <rect
        x="16"
        y="16"
        width="480"
        height="480"
        rx="88"
        fill="#FAFAFA"
        stroke="#BDBDBD"
        strokeWidth="32"
        strokeDasharray="56 40"
      />
      <path
        d="M200 196a56 56 0 1 1 72 54v34"
        stroke="#9E9E9E"
        strokeWidth="34"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="272" cy="350" r="22" fill="#9E9E9E" />
    </svg>
  );
}
